import axios, { type AxiosInstance, type AxiosResponse } from 'axios'

import { FASTAPI_CONFIG } from '@/config/fastapi'
import { useAuthStore } from '@/stores/authStore'

/**
 * File API Service for file management operations
 */
class FileApiService {
  private client: AxiosInstance

  constructor() {
    this.client = axios.create({
      baseURL: FASTAPI_CONFIG.BASE_URL + '/api/v1/files',
      timeout: 30000,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json'
      }
    })

    // Add request interceptor to include auth token
    this.client.interceptors.request.use(
      (config) => {
        try {
          // Get token from auth store
          const authStore = useAuthStore()
          const token = authStore.getAccessToken()

          if (token) {
            config.headers.Authorization = `Bearer ${token}`
          }
        } catch (error) {
          console.warn('Failed to get auth token for File API request:', error)
        }

        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    // Add response interceptor to handle token refresh
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true

          try {
            const authStore = useAuthStore()

            // If user is not initialized yet, don't show dialog - let router guard handle redirect
            if (!authStore.isInitialized || !authStore.currentUser) {
              console.log(
                'File API: 401 error during initialization, will redirect to login page'
              )
              return Promise.reject(error)
            }
            const success = await authStore.refreshAccessToken()

            if (success) {
              // Retry original request with new token
              const newToken = authStore.getAccessToken()
              if (newToken) {
                originalRequest.headers.Authorization = `Bearer ${newToken}`
                return this.client(originalRequest)
              }
            }
          } catch (refreshError) {
            // Refresh failed, show login dialog
            const authStore = useAuthStore()
            await authStore.logout()

            // Show toast notification first
            try {
              const { useToastStore } = await import(
                '@/platform/updates/common/toastStore'
              )
              const toastStore = useToastStore()
              toastStore.add({
                severity: 'warn',
                summary: '需要登录',
                detail: '您的登录已过期，请重新登录',
                life: 3000
              })
            } catch (toastError) {
              console.warn('Failed to show toast notification:', toastError)
            }

            // 精简模式 /workflow2 已显示登录弹窗，不重定向避免循环刷新
            if (!window.location.pathname.startsWith('/workflow2')) {
              console.log('登录已过期，直接跳转到登录页面')
              window.location.href = '/login'
            }
          }
        }

        return Promise.reject(error)
      }
    )
  }

  // Generic GET request
  async get(endpoint: string, params?: any): Promise<any> {
    const response: AxiosResponse = await this.client.get(endpoint, { params })
    return response.data
  }

  // Generic POST request
  async post(endpoint: string, data?: any): Promise<any> {
    const response: AxiosResponse = await this.client.post(endpoint, data)
    return response.data
  }

  // Generic PUT request
  async put(endpoint: string, data?: any, config?: any): Promise<any> {
    const response: AxiosResponse = await this.client.put(
      endpoint,
      data,
      config
    )
    return response.data
  }

  // Generic DELETE request
  async delete(endpoint: string, params?: any): Promise<any> {
    const response: AxiosResponse = await this.client.delete(endpoint, {
      params
    })
    return response.data
  }

  // File-specific methods

  /**
   * 更新文件使用时间
   * @param fileId 文件ID
   */
  async updateFileUsage(fileId: number): Promise<any> {
    return this.put('/use', null, { params: { file_id: fileId } })
  }

  /**
   * 获取单个文件详情
   * @param fileId 文件ID
   */
  async getFileDetail(fileId: number): Promise<any> {
    return this.get('/detail', { file_id: fileId })
  }

  /**
   * 删除文件
   * @param fileId 文件ID
   */
  async deleteFile(fileId: number): Promise<any> {
    return this.delete('/delete', { file_id: fileId })
  }
}

// Export singleton instance
export const fileApiService = new FileApiService()

// Export function to get the service
export function useFileApiService(): FileApiService {
  return fileApiService
}
